"use client";

import { useEffect, useMemo, useRef } from "react";
import "highlight.js/styles/github.css";
import { markdownWithLatexToHtml } from "@/lib/markdown-latex";
import { normalizeGeneratedText } from "@/lib/normalize-generated-text";
import {
  normalizeMermaidChart,
  splitMarkdownAndMermaid,
  splitMermaidCharts,
  isLikelyMermaid,
} from "@/lib/learning-content";
import { dynamic } from "@/src/lib/next-compat";

const defaultProseClass =
  "prose prose-slate max-w-none prose-p:text-slate-700 prose-li:text-slate-700";

const MermaidRenderer = dynamic(
  () => import("@/components/MermaidRenderer").then((m) => m.MermaidRenderer),
  {
    ssr: false,
    loading: () => (
      <div className="my-6 rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm not-prose">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
          <span className="text-sm font-medium text-slate-700">Loading diagram engine...</span>
        </div>
      </div>
    ),
  },
);

type LearningRichContentProps = {
  content: string;
  className?: string;
  /** Extra wrapper classes (e.g. learning-content space-y-6) */
  segmentGapClassName?: string;
};

/**
 * Markdown + LaTeX + :::clarification / :::info / :::warning / :::checklist (via markdown-latex) + ```mermaid``` / :::mermaid … ::: blocks.
 */
export function LearningRichContent({
  content,
  className = defaultProseClass,
  segmentGapClassName = "space-y-4",
}: LearningRichContentProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const normalizedContent = useMemo(() => normalizeGeneratedText(content || ""), [content]);
  const segments = useMemo(() => splitMarkdownAndMermaid(normalizedContent), [normalizedContent]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const onClick = async (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const button = target.closest(".learning-copy-btn") as HTMLButtonElement | null;
      if (!button) return;
      const raw = button.getAttribute("data-copy");
      if (!raw) return;
      try {
        await navigator.clipboard.writeText(decodeURIComponent(raw));
        const prev = button.textContent;
        button.textContent = "Copied";
        setTimeout(() => {
          button.textContent = prev ?? "Copy";
        }, 1200);
      } catch {
        // no-op: clipboard can fail outside secure context
      }
    };

    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, []);

  return (
    <div ref={rootRef} className={`${className} ${segmentGapClassName}`}>
      {segments.map((seg, i) => {
        if (seg.type === "md") {
          return (
            <div
              key={i}
              dangerouslySetInnerHTML={{ __html: markdownWithLatexToHtml(seg.value) }}
            />
          );
        }
        const charts = splitMermaidCharts(seg.value);
        return charts.map((chart, j) => (
          <MermaidRenderer key={`${i}-${j}`} chart={normalizeMermaidChart(chart)} />
        ));
      })}
    </div>
  );
}

type LearningMarkdownHtmlProps = {
  content: string;
  className?: string;
};

/** Markdown + LaTeX + directives only (no fenced mermaid). Kept for tiny one-liner fields if needed. */
export function LearningMarkdownHtml({ content, className = defaultProseClass }: LearningMarkdownHtmlProps) {
  if (!content?.trim()) return null;
  const normalizedContent = normalizeGeneratedText(content);
  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: markdownWithLatexToHtml(normalizedContent) }}
    />
  );
}

type LearningMermaidDiagramProps = {
  chart: string;
  unavailableClassName?: string;
};

/** Single diagram field (e.g. remedial structured `diagram`); soft-fail if not parseable mermaid. */
export function LearningMermaidDiagram({
  chart,
  unavailableClassName = "rounded-lg border border-dashed border-slate-300 p-3 text-sm text-slate-500",
}: LearningMermaidDiagramProps) {
  if (!isLikelyMermaid(chart)) {
    return <div className={unavailableClassName}>Diagram unavailable</div>;
  }
  return <MermaidRenderer chart={normalizeMermaidChart(chart)} />;
}
